/**
 * The resignation leave payout PREVIEW (`GET /api/resignation-payout/{id}`). The formula is the backend's; this file only types it
 * and decides how each figure reads. Pure, so it runs under vitest's `node`; the query hook lives in the panel because `lib/api` needs a `window`.
 * ⛔ A null figure is UNKNOWN, never zero: render "—" plus the reason, never 0.00.
 */
/** Decimals arrive as strings; null means the server could not know it. */
type Num = string | null

interface PayoutLeave {
  kind: 'CL' | 'PSL' | 'AL'
  start_date: string
  end_date: string
  days: string
  half_day: boolean
  is_future: boolean
}

/** A maternity or child-care break payout payroll raised; read-only on this page. */
export interface BreakPayout {
  id: string
  break_start: string
  status: 'Closed' | 'Paid'
  al_days: string
  al_day_value: string
  total: string
  cl_psl_amount: string
  salary_run_id: string | null
  paid_at: string | null
}

export interface ResignationPayout {
  employee_id: string
  resign_date: string
  resign_date_source: 'query' | 'stored' | 'today'
  work_type: string
  full_time: boolean
  rate_type: string | null
  /** Worked work days over the year's work days, holidays excluded; the basis for AL, CL and PSL alike. */
  year_fraction: string
  worked_days: number
  year_work_days: number
  day_value: Num
  al_day_value: Num
  cl_balance_raw: Num
  cl_future: string
  cl_balance: Num
  cl_used: string
  cl_accrued: string
  cl_bonus: Num
  cl_payout_days: Num
  cl_payout: Num
  psl_balance_raw: Num
  psl_future: string
  psl_balance: Num
  psl_used: string
  psl_accrued: string
  psl_payout_days: Num
  psl_payout: Num
  al_type: string
  al_allocation: Num
  al_balance_raw: Num
  al_future: string
  al_balance: Num
  al_used: string
  al_accrued: Num
  al_unaccrued: Num
  al_net: Num
  al_payout_days: Num
  al_deduct_days: Num
  al_amount: Num
  total: Num
  /** A settled Resignation settlement already paid this year's AL, so the close-out pays none. */
  al_settled?: boolean
  reasons: string[]
  leave: PayoutLeave[]
  break_payouts?: BreakPayout[]
}

export const UNKNOWN = '—'

/** Whether a break payout has been carried by an issued run; anything else waits for the next final. */
export function breakPayoutState(row: Pick<BreakPayout, 'status' | 'paid_at'>): 'paid' | 'pending' {
  return row.status === 'Paid' && row.paid_at ? 'paid' : 'pending'
}

/** The break payouts to list, newest first; an older server that sends none lists nothing. */
export function breakPayoutsOf(data: Pick<ResignationPayout, 'break_payouts'>): BreakPayout[] {
  return [...(data.break_payouts ?? [])].sort((a, b) => b.break_start.localeCompare(a.break_start))
}

/** The year-elapsed figure; the work-day count only when the server sent one, since 0 / 0 would assert a basis it does not know. */
export function yearElapsed(
  payout: Pick<ResignationPayout, 'year_fraction' | 'worked_days' | 'year_work_days'>,
): { percent: string; workDays: { worked: number; total: number } | null } {
  const percent = `${(Number(payout.year_fraction) * 100).toFixed(1)}%`
  const total = payout.year_work_days ?? 0
  return { percent, workDays: total > 0 ? { worked: payout.worked_days ?? 0, total } : null }
}

/** Which way the signed total moves the final pay (`InfoEdit:218`). */
export function payoutDirection(total: Num): 'add' | 'deduct' | 'unknown' {
  if (total === null) return 'unknown'
  return Number(total) >= 0 ? 'add' : 'deduct'
}

/** A stored close-out (`GET /api/resignation-payout/{id}/closeout`). */
export interface PayoutCloseout {
  id: string
  employee_id: string
  resign_date: string
  status: 'Closed' | 'Paid' | 'Void'
  /** Set when the close-out is paid by a one-off payslip: issuing or voiding that one-off settles it. */
  one_off_payslip_id?: string | null
  total: string
  salary_run_id: string | null
  paid_at: string | null
  closed_at: string
}

/** Which buttons the panel offers. Close out is DISABLED, never enabled on a guess: it needs `salary.edit`, a computable figure and a recorded date. */
export function closeoutActions(args: {
  canEdit: boolean
  closeout: PayoutCloseout | null | undefined
  total: Num
  reasons: string[]
  previewDate: string
  storedDate: string | null
}): { showClose: boolean; closeEnabled: boolean; showPaid: boolean; showVoid: boolean } {
  const { canEdit, closeout, total, reasons, previewDate, storedDate } = args
  const live = closeout != null && closeout.status !== 'Void'
  const settledByHand = closeout?.status === 'Closed' && !closeout.one_off_payslip_id
  return {
    showClose: canEdit && !live,
    closeEnabled:
      canEdit &&
      !live &&
      total !== null &&
      reasons.length === 0 &&
      storedDate !== null &&
      previewDate === storedDate,
    showPaid: canEdit && settledByHand,
    showVoid: canEdit && settledByHand,
  }
}
